import axios from 'axios';
import { getWagerWizDisplayOptions, SPORTSBOOKS, DEFAULT_SPORTSBOOKS } from '../constants';
import { pushSportsbooksToQueryField } from '../sportsbookAliases';

const queryRowsSample = [
	{
		league: [{ value: 'NBA', label: 'NBA' }],
		date: '2025-11-27T18:26:12.000Z',
		minMaxOdds: [-1000, 1000],
		minMaxEv: [5, 10],
		minMaxEvFilterOpen: false,
		minMaxOddsFilterOpen: false,
		sport: [{ value: 'basketball', label: 'basketball' }],
		book: ['DraftKings', 'bet365'],
		sharp: ['Betway (Alaska)', 'Pinnacle'],
		market: [],
		marketType: [],
		name: 'test - renamed',
		id: 'ideb4522f64db1d',
		default: false,
		limit: '',
		hide_alternates: false,
		dateDifference: 308,
	},
];

/**
 * Makes progressive EV API calls, calling onProgress as each endpoint responds
 * @param {Array} queryRows - Array of query row objects
 * @param {string} email - User email
 * @param {boolean} is_live - Whether to fetch live data
 * @param {string} token - Auth token
 * @param {number} page - Page number
 * @param {number} limit - Results per page
 * @param {Function} onProgress - Callback called with (endpoint, data) as each endpoint responds
 * @returns {Promise<Object>} Final combined result
 */
export const runEvScreenPaginatedProgressive = async (queryRows, email, is_live, token, page, limit, onProgress) => {
	try {
		// Determine which endpoints to call based on markets
		const endpoints = determineEvEndpoints(queryRows);
		
		// Track all responses
		const allResponses = [];
		let scanLimitExceeded = false;
		
		// Make requests and handle each as it completes
		const requestPromises = endpoints.map(async (endpoint) => {
			try {
				const response = await axios.post(
					`${process.env.REACT_APP_BACKEND_URL}${endpoint}?email=${email}&is_live=${is_live}&page=${page}&limit=${limit}`,
					{ queryRows: queryRows },
					{
						headers: {
							Authorization: `Bearer ${token}`,
						},
					}
				);
				
				allResponses.push({ endpoint, response, error: null });
				
				// Check for scan limit error
				if (response.data === 'exceeded daily scans limit' || 
					(typeof response.data === 'string' && response.data === 'exceeded daily scans limit')) {
					scanLimitExceeded = true;
					if (onProgress) {
						onProgress(endpoint, 'exceeded daily scans limit');
					}
					return;
				}
				
				// Extract data from response
				let endpointData = [];
				if (response.data && typeof response.data === 'object') {
					if (response.data.data && Array.isArray(response.data.data)) {
						endpointData = response.data.data;
					} else if (Array.isArray(response.data)) {
						endpointData = response.data;
					}
				}
				
				// Call progress callback immediately with this endpoint's data
				if (onProgress) {
					onProgress(endpoint, endpointData);
				}
				
				return { endpoint, data: endpointData, response: response.data };
			} catch (error) {
				console.error(`Error calling ${endpoint}:`, error);
				allResponses.push({ endpoint, response: null, error });
				
				// Check if it's a daily scan limit error
				if (error.response && typeof error.response.data === 'string' && error.response.data === 'exceeded daily scans limit') {
					scanLimitExceeded = true;
					if (onProgress) {
						onProgress(endpoint, 'exceeded daily scans limit');
					}
				} else if (onProgress) {
					// Call progress with empty data for failed endpoint
					onProgress(endpoint, []);
				}
				
				return { endpoint, data: [], error };
			}
		});
		
		// Wait for all requests to complete (but onProgress is called as each finishes)
		await Promise.allSettled(requestPromises);
		
		// Check if any response indicates daily scan limit exceeded
		if (scanLimitExceeded) {
			return 'exceeded daily scans limit';
		}
		
		// Return final combined result for backward compatibility
		let combinedData = [];
		let combinedPagination = {
			page: page,
			limit: limit,
			total: 0,
			totalPages: 0,
			hasNext: false,
			hasPrev: false
		};
		
		for (const { response } of allResponses) {
			if (response && response.data && typeof response.data === 'object') {
				if (response.data.data && Array.isArray(response.data.data)) {
					combinedData = combinedData.concat(response.data.data);
					if (response.data.pagination) {
						combinedPagination.total += response.data.pagination.total || 0;
						if (response.data.pagination.hasNext) {
							combinedPagination.hasNext = true;
						}
						if (response.data.pagination.hasPrev) {
							combinedPagination.hasPrev = true;
						}
					}
				} else if (Array.isArray(response.data)) {
					combinedData = combinedData.concat(response.data);
				}
			}
		}
		
		// Sort combined results by EV (descending)
		combinedData.sort((a, b) => {
			const evA = a.ev || a.ev_over || 0;
			const evB = b.ev || b.ev_over || 0;
			return evB - evA;
		});
		
		combinedPagination.total = combinedData.length;
		combinedPagination.totalPages = Math.ceil(combinedPagination.total / limit);
		
		return {
			data: combinedData,
			pagination: combinedPagination
		};
	} catch (e) {
		console.error('ev screen progressive e: ', e);
		return false;
	}
};

/**
 * Determines which EV endpoints to call based on the markets in queryRows
 * @param {Array} queryRows - Array of query row objects with market filters
 * @returns {Array} Array of endpoint paths to call
 */
function determineEvEndpoints(queryRows) {
	if (!queryRows || queryRows.length === 0) {
		// If no query rows, call all endpoints
		return ['/ev_results', '/ev_results_moneyline', '/ev_results_point_spread'];
	}

	// Collect all market types from all query rows
	const allMarketTypes = [];
	for (const row of queryRows) {
		if (row.marketType && Array.isArray(row.marketType) && row.marketType.length > 0) {
			// Market types can be strings or objects with value property
			for (const mktType of row.marketType) {
				const marketTypeValue = typeof mktType === 'string' ? mktType : (mktType.value || mktType.label || mktType);
				if (marketTypeValue && !allMarketTypes.includes(marketTypeValue)) {
					allMarketTypes.push(marketTypeValue);
				}
			}
		}
	}

	// If market types are explicitly set to only "Player Props", skip moneyline and point spread endpoints
	const onlyPlayerProps = allMarketTypes.length > 0 && 
		allMarketTypes.length === 1 && 
		(allMarketTypes[0] === 'Player Props' || allMarketTypes[0].toLowerCase() === 'player props');

	// Collect all markets from all query rows
	const allMarkets = [];
	for (const row of queryRows) {
		if (row.market && Array.isArray(row.market) && row.market.length > 0) {
			// Markets can be strings or objects with value property
			for (const mkt of row.market) {
				const marketValue = typeof mkt === 'string' ? mkt : (mkt.value || mkt.label || mkt);
				if (marketValue && !allMarkets.includes(marketValue)) {
					allMarkets.push(marketValue);
				}
			}
		}
	}

	// If no markets specified, call all endpoints (unless only Player Props)
	if (allMarkets.length === 0) {
		if (onlyPlayerProps) {
			return ['/ev_results'];
		}
		return ['/ev_results', '/ev_results_moneyline', '/ev_results_point_spread'];
	}

	// Check which market types are present
	const hasMoneyline = allMarkets.some(m => 
		m === 'Moneyline' || 
		m.toLowerCase() === 'moneyline'
	);
	
	const hasPointSpread = allMarkets.some(m => 
		m === 'Point Spread' || 
		m === 'Asian Handicap' ||
		m.toLowerCase() === 'point spread' ||
		m.toLowerCase() === 'asian handicap'
	);
	
	// Determine which other markets exist (not Moneyline, Point Spread, or Asian Handicap)
	const hasOtherMarkets = allMarkets.some(m => {
		const mLower = m.toLowerCase();
		return mLower !== 'moneyline' && 
		       mLower !== 'point spread' && 
		       mLower !== 'asian handicap';
	});

	const endpoints = [];
	
	// Add endpoints based on what markets are present
	// Skip moneyline and point spread if only Player Props market type is selected
	if (hasMoneyline && !onlyPlayerProps) {
		endpoints.push('/ev_results_moneyline');
	}
	
	if (hasPointSpread && !onlyPlayerProps) {
		endpoints.push('/ev_results_point_spread');
	}
	
	if (hasOtherMarkets || onlyPlayerProps) {
		endpoints.push('/ev_results');
	}

	// If no endpoints determined (shouldn't happen), default to all (unless only Player Props)
	if (endpoints.length === 0) {
		if (onlyPlayerProps) {
			return ['/ev_results'];
		}
		return ['/ev_results', '/ev_results_moneyline', '/ev_results_point_spread'];
	}

	return endpoints;
}

export const runEvScreen = async (email, filters, is_live = false, token, page = 1, limit = 50) => {
	try {
		const queryRows = [];

		let fltrs = filters;
		console.log('fltrs: ', fltrs[0]?.sharpBooks?.[0]);
		if (!fltrs) {
			const userdb = JSON.parse(window.localStorage.getItem('userdb'));
			const preferredSportsbooks = userdb.preferred_sportsbooks;

			let sportsbooks = [];
			if (preferredSportsbooks?.length > 0) {
				for (const bk of preferredSportsbooks) {
					sportsbooks.push({ label: bk, value: bk });
				}
			} else {
				sportsbooks = [{ label: 'bet365', value: 'bet365' }];
			}

			fltrs = [
				{
					sports: [],
					leagues: [],
					sportsbooks: sportsbooks,
					sharpBooks: getWagerWizDisplayOptions(),
					markets: [],
					marketTypes: [],
					minMaxEv: [-1000, 1000],
					minMaxOdds: [-100000, 100000],
					date: new Date(),
				},
			];
		}

		for (const row of fltrs) {
			const queryRow = {};
			// Ensure dateDifference is set - calculate from date if missing
			if (row.dateDifference === undefined || row.dateDifference === null) {
				if (row.date) {
					const currentTime = Date.now();
					const fourHoursMs = 4 * 60 * 60 * 1000;
					const dateTime = new Date(row.date).getTime();
					const diffTime = dateTime - (currentTime - fourHoursMs);
					queryRow.dateDifference = Math.floor(diffTime / (1000 * 60 * 60 * 24));
				} else {
					// Default to 7 days if no date
					queryRow.dateDifference = 7;
				}
			} else {
				queryRow.dateDifference = row.dateDifference;
			}

			const currentTime = Date.now();
			const fourHoursMs = 4 * 60 * 60 * 1000;
			const futureTime = currentTime - fourHoursMs + queryRow.dateDifference * 24 * 60 * 60 * 1000;

			try {
				const futureDate = new Date(futureTime);
				queryRow.date = new Date(futureDate).toISOString();
			} catch (e) {
				const futureDate = new Date();
				queryRow.date = new Date(futureDate).toISOString();
			}

			// const inputDate = new Date(row.date);
			// const today = new Date();

			// // Normalize both to midnight for date-only comparison
			// inputDate.setHours(0, 0, 0, 0);
			// today.setHours(0, 0, 0, 0);

			// // Use today's date if inputDate is in the past
			// const finalDate = inputDate < today ? today : inputDate;
			// queryRow.date = new Date(finalDate).toISOString();

			queryRow.hide_alternates = row.hide_alternates;
			queryRow.limit = row.limit || '';
			queryRow.default = false;
			queryRow.name = 'temp';
			queryRow.id = 'temp';
			queryRow.league = [];
			queryRow.sport = [];
			queryRow.market = [];
			queryRow.marketType = [];
			queryRow.book = [];
			queryRow.sharp = [];
			queryRow.live = is_live;
			queryRow.minMaxOdds = row.minMaxOdds || [-100000, 100000];
			// Convert old default values to new default values
			if (queryRow.minMaxOdds[0] === -10000 && queryRow.minMaxOdds[1] === 10000) {
				queryRow.minMaxOdds = [-100000, 100000];
			}
			if (queryRow.minMaxOdds[0] === '' || queryRow.minMaxOdds[0] === null || queryRow.minMaxOdds[0] === undefined) {
				queryRow.minMaxOdds[0] = -100000;
			}
			if (queryRow.minMaxOdds[1] === '' || queryRow.minMaxOdds[1] === null || queryRow.minMaxOdds[1] === undefined) {
				queryRow.minMaxOdds[1] = 100000;
			}
			queryRow.minMaxEv = row.minMaxEv || [-1000, 1000];
			if (queryRow.minMaxEv[0] === '' || queryRow.minMaxEv[0] === null || queryRow.minMaxEv[0] === undefined) {
				queryRow.minMaxEv[0] = -1000;
			}
			if (queryRow.minMaxEv[1] === '' || queryRow.minMaxEv[1] === null || queryRow.minMaxEv[1] === undefined) {
				queryRow.minMaxEv[1] = 1000;
			}
			queryRow.minMaxEv = queryRow.minMaxEv.map((e) => Number(e));

			for (const league of row.leagues) {
				queryRow.league.push(league);
			}
			for (const sport of row.sports) {
				queryRow.sport.push(sport);
			}
			for (const mkt of row.markets) {
				queryRow.market.push(mkt);
			}
			for (const mkt of row.marketTypes) {
				queryRow.marketType.push(mkt);
			}
			// If no sportsbooks are selected, include all available sportsbooks
			const sportsbooksToProcess = row.sportsbooks && row.sportsbooks.length > 0 ? row.sportsbooks : DEFAULT_SPORTSBOOKS;
			
			for (const mkt of sportsbooksToProcess) {
				pushSportsbooksToQueryField(queryRow, 'book', mkt);
			}
			// Fall back to WagerWiz defaults when sharpBooks is empty — without a sharp
			// reference the backend returns no results.
			const sharpBooksToProcess = row.sharpBooks && row.sharpBooks.length > 0 ? row.sharpBooks : getWagerWizDisplayOptions();
			for (const mkt of sharpBooksToProcess) {
				pushSportsbooksToQueryField(queryRow, 'sharp', mkt);
			}

			queryRows.push(queryRow);
		}

		// Determine which endpoints to call based on markets
		const endpoints = determineEvEndpoints(queryRows);
		
		// Make parallel requests to all determined endpoints
		const requests = endpoints.map(endpoint =>
			axios.post(
				`${process.env.REACT_APP_BACKEND_URL}${endpoint}?email=${email}&is_live=${is_live}&page=${page}&limit=${limit}`,
				{ queryRows: queryRows },
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			).catch(error => {
				// Handle errors gracefully - return empty result for failed endpoint
				console.error(`Error calling ${endpoint}:`, error);
				// Check if it's a daily scan limit error
				if (error.response && typeof error.response.data === 'string' && error.response.data === 'exceeded daily scans limit') {
					return { data: 'exceeded daily scans limit' };
				}
				return { data: { data: [], pagination: { page: 1, limit: 0, total: 0, totalPages: 0, hasNext: false, hasPrev: false } } };
			})
		);

		const responses = await Promise.all(requests);
		
		// Check if any response indicates daily scan limit exceeded
		const scanLimitExceeded = responses.some(res => 
			res.data === 'exceeded daily scans limit' || 
			(res.data && typeof res.data === 'string' && res.data === 'exceeded daily scans limit')
		);
		
		if (scanLimitExceeded) {
			return 'exceeded daily scans limit';
		}

		// Combine all results
		let combinedData = [];
		let combinedPagination = {
			page: page,
			limit: limit,
			total: 0,
			totalPages: 0,
			hasNext: false,
			hasPrev: false
		};

		for (const response of responses) {
			if (response.data && typeof response.data === 'object') {
				// Handle paginated response format
				if (response.data.data && Array.isArray(response.data.data)) {
					combinedData = combinedData.concat(response.data.data);
					// Aggregate pagination info
					if (response.data.pagination) {
						combinedPagination.total += response.data.pagination.total || 0;
						// hasNext is true if any endpoint has more pages
						if (response.data.pagination.hasNext) {
							combinedPagination.hasNext = true;
						}
						// hasPrev is true if any endpoint has previous pages
						if (response.data.pagination.hasPrev) {
							combinedPagination.hasPrev = true;
						}
					}
				} else if (Array.isArray(response.data)) {
					// Legacy format (direct array)
					combinedData = combinedData.concat(response.data);
				}
			}
		}

		// Sort combined results by EV (descending) if available
		combinedData.sort((a, b) => {
			const evA = a.ev || a.ev_over || 0;
			const evB = b.ev || b.ev_over || 0;
			return evB - evA;
		});

		// Calculate combined pagination
		combinedPagination.total = combinedData.length;
		combinedPagination.totalPages = Math.ceil(combinedPagination.total / limit);

		// Return combined results in paginated format
		return {
			data: combinedData,
			pagination: combinedPagination
		};
	} catch (e) {
		console.error('ev screen e: ', e);
		return false;
	}
};

/**
 * Free (unauthenticated) variant of runEvScreen.
 * Calls /ev_results_free with no auth header and no email parameter.
 */
export const runEvScreenFree = async (filters, is_live = false, page = 1, limit = 50) => {
	try {
		const queryRows = [];

		let fltrs = filters;
		if (!fltrs) {
			fltrs = [
				{
					sports: [],
					leagues: [],
					sportsbooks: DEFAULT_SPORTSBOOKS,
					sharpBooks: getWagerWizDisplayOptions(),
					markets: [],
					marketTypes: [],
					minMaxEv: [-1000, 1000],
					minMaxOdds: [-100000, 100000],
					date: new Date(),
				},
			];
		}

		for (const row of fltrs) {
			const queryRow = {};
			if (row.dateDifference === undefined || row.dateDifference === null) {
				if (row.date) {
					const currentTime = Date.now();
					const fourHoursMs = 4 * 60 * 60 * 1000;
					const dateTime = new Date(row.date).getTime();
					const diffTime = dateTime - (currentTime - fourHoursMs);
					queryRow.dateDifference = Math.floor(diffTime / (1000 * 60 * 60 * 24));
				} else {
					queryRow.dateDifference = 7;
				}
			} else {
				queryRow.dateDifference = row.dateDifference;
			}

			const currentTime = Date.now();
			const fourHoursMs = 4 * 60 * 60 * 1000;
			const futureTime = currentTime - fourHoursMs + queryRow.dateDifference * 24 * 60 * 60 * 1000;

			try {
				queryRow.date = new Date(futureTime).toISOString();
			} catch (e) {
				queryRow.date = new Date().toISOString();
			}

			queryRow.hide_alternates = row.hide_alternates;
			queryRow.limit = row.limit || '';
			queryRow.default = false;
			queryRow.name = 'temp';
			queryRow.id = 'temp';
			queryRow.league = [];
			queryRow.sport = [];
			queryRow.market = [];
			queryRow.marketType = [];
			queryRow.book = [];
			queryRow.sharp = [];
			queryRow.live = is_live;
			queryRow.minMaxOdds = row.minMaxOdds || [-100000, 100000];
			if (queryRow.minMaxOdds[0] === -10000 && queryRow.minMaxOdds[1] === 10000) {
				queryRow.minMaxOdds = [-100000, 100000];
			}
			if (queryRow.minMaxOdds[0] === '' || queryRow.minMaxOdds[0] === null || queryRow.minMaxOdds[0] === undefined) {
				queryRow.minMaxOdds[0] = -100000;
			}
			if (queryRow.minMaxOdds[1] === '' || queryRow.minMaxOdds[1] === null || queryRow.minMaxOdds[1] === undefined) {
				queryRow.minMaxOdds[1] = 100000;
			}
			queryRow.minMaxEv = row.minMaxEv || [-1000, 1000];
			if (queryRow.minMaxEv[0] === '' || queryRow.minMaxEv[0] === null || queryRow.minMaxEv[0] === undefined) {
				queryRow.minMaxEv[0] = -1000;
			}
			if (queryRow.minMaxEv[1] === '' || queryRow.minMaxEv[1] === null || queryRow.minMaxEv[1] === undefined) {
				queryRow.minMaxEv[1] = 1000;
			}
			queryRow.minMaxEv = queryRow.minMaxEv.map((e) => Number(e));

			for (const league of row.leagues) {
				queryRow.league.push(league);
			}
			for (const sport of row.sports) {
				queryRow.sport.push(sport);
			}
			for (const mkt of row.markets) {
				queryRow.market.push(mkt);
			}
			for (const mkt of row.marketTypes) {
				queryRow.marketType.push(mkt);
			}
			const sportsbooksToProcess = row.sportsbooks && row.sportsbooks.length > 0 ? row.sportsbooks : DEFAULT_SPORTSBOOKS;

			for (const mkt of sportsbooksToProcess) {
				pushSportsbooksToQueryField(queryRow, 'book', mkt);
			}
			// Fall back to WagerWiz defaults when sharpBooks is empty — without a sharp
			// reference the backend returns no results.
			const sharpBooksToProcess = row.sharpBooks && row.sharpBooks.length > 0 ? row.sharpBooks : getWagerWizDisplayOptions();
			for (const mkt of sharpBooksToProcess) {
				pushSportsbooksToQueryField(queryRow, 'sharp', mkt);
			}

			queryRows.push(queryRow);
		}

		let response;
		try {
			response = await axios.post(
				`${process.env.REACT_APP_BACKEND_URL}/ev_results_free?is_live=${is_live}&page=${page}&limit=${limit}`,
				{ queryRows },
			);
		} catch (error) {
			if (error?.response && typeof error.response.data === 'string' && error.response.data === 'exceeded daily scans limit') {
				return 'exceeded daily scans limit';
			}
			throw error;
		}

		if (typeof response.data === 'string' && response.data === 'exceeded daily scans limit') {
			return 'exceeded daily scans limit';
		}

		if (response.data && typeof response.data === 'object') {
			if (response.data.data && Array.isArray(response.data.data)) {
				const combinedData = response.data.data;
				combinedData.sort((a, b) => {
					const evA = a.ev || a.ev_over || 0;
					const evB = b.ev || b.ev_over || 0;
					return evB - evA;
				});
				return {
					data: combinedData,
					pagination: response.data.pagination || {
						page,
						limit,
						total: combinedData.length,
						totalPages: Math.ceil(combinedData.length / limit),
						hasNext: false,
						hasPrev: page > 1,
					},
				};
			}
			if (Array.isArray(response.data)) {
				const sorted = [...response.data].sort((a, b) => (b.ev || b.ev_over || 0) - (a.ev || a.ev_over || 0));
				return {
					data: sorted,
					pagination: { page, limit, total: sorted.length, totalPages: 1, hasNext: false, hasPrev: false },
				};
			}
		}

		return { data: [], pagination: { page, limit, total: 0, totalPages: 0, hasNext: false, hasPrev: false } };
	} catch (e) {
		console.error('ev screen free e: ', e);
		return false;
	}
};

export const runEvScreenPaginated = async (email, filters, is_live = false, token, page = 1, limit = 50) => {
	try {
		const queryRows = [];

		let fltrs = filters;
		console.log('fltrs: ', fltrs[0]?.sharpBooks?.[0]);
		if (!fltrs) {
			const userdb = JSON.parse(window.localStorage.getItem('userdb'));
			const preferredSportsbooks = userdb.preferred_sportsbooks;

			let sportsbooks = [];
			if (preferredSportsbooks?.length > 0) {
				for (const bk of preferredSportsbooks) {
					sportsbooks.push({ label: bk, value: bk });
				}
			} else {
				sportsbooks = [{ label: 'bet365', value: 'bet365' }];
			}

			fltrs = [
				{
					sports: [],
					leagues: [],
					sportsbooks: sportsbooks,
					sharpBooks: getWagerWizDisplayOptions(),
					markets: [],
					marketTypes: [],
					minMaxEv: [-1000, 1000],
					minMaxOdds: [-100000, 100000],
					date: new Date(),
				},
			];
		}

		for (const row of fltrs) {
			const queryRow = {};
			// Ensure dateDifference is set - calculate from date if missing
			if (row.dateDifference === undefined || row.dateDifference === null) {
				if (row.date) {
					const currentTime = Date.now();
					const fourHoursMs = 4 * 60 * 60 * 1000;
					const dateTime = new Date(row.date).getTime();
					const diffTime = dateTime - (currentTime - fourHoursMs);
					queryRow.dateDifference = Math.floor(diffTime / (1000 * 60 * 60 * 24));
				} else {
					// Default to 7 days if no date
					queryRow.dateDifference = 7;
				}
			} else {
				queryRow.dateDifference = row.dateDifference;
			}

			const currentTime = Date.now();
			const fourHoursMs = 4 * 60 * 60 * 1000;
			const futureTime = currentTime - fourHoursMs + queryRow.dateDifference * 24 * 60 * 60 * 1000;

			try {
				const futureDate = new Date(futureTime);
				queryRow.date = new Date(futureDate).toISOString();
			} catch (e) {
				const futureDate = new Date();
				queryRow.date = new Date(futureDate).toISOString();
			}

			queryRow.hide_alternates = row.hide_alternates;
			queryRow.limit = row.limit || '';
			queryRow.default = false;
			queryRow.name = 'temp';
			queryRow.id = 'temp';
			queryRow.league = [];
			queryRow.sport = [];
			queryRow.market = [];
			queryRow.marketType = [];
			queryRow.book = [];
			queryRow.sharp = [];
			queryRow.live = is_live;
			queryRow.minMaxOdds = row.minMaxOdds || [-100000, 100000];
			// Convert old default values to new default values
			if (queryRow.minMaxOdds[0] === -10000 && queryRow.minMaxOdds[1] === 10000) {
				queryRow.minMaxOdds = [-100000, 100000];
			}
			if (queryRow.minMaxOdds[0] === '' || queryRow.minMaxOdds[0] === null || queryRow.minMaxOdds[0] === undefined) {
				queryRow.minMaxOdds[0] = -100000;
			}
			if (queryRow.minMaxOdds[1] === '' || queryRow.minMaxOdds[1] === null || queryRow.minMaxOdds[1] === undefined) {
				queryRow.minMaxOdds[1] = 100000;
			}
			queryRow.minMaxEv = row.minMaxEv || [-1000, 1000];
			if (queryRow.minMaxEv[0] === '' || queryRow.minMaxEv[0] === null || queryRow.minMaxEv[0] === undefined) {
				queryRow.minMaxEv[0] = -1000;
			}
			if (queryRow.minMaxEv[1] === '' || queryRow.minMaxEv[1] === null || queryRow.minMaxEv[1] === undefined) {
				queryRow.minMaxEv[1] = 1000;
			}
			queryRow.minMaxEv = queryRow.minMaxEv.map((e) => Number(e));

			for (const league of row.leagues) {
				queryRow.league.push(league);
			}
			for (const sport of row.sports) {
				queryRow.sport.push(sport);
			}
			for (const mkt of row.markets) {
				queryRow.market.push(mkt);
			}
			for (const mkt of row.marketTypes) {
				queryRow.marketType.push(mkt);
			}
			// If no sportsbooks are selected, include all available sportsbooks
			const sportsbooksToProcess = row.sportsbooks && row.sportsbooks.length > 0 ? row.sportsbooks : DEFAULT_SPORTSBOOKS;
			
			for (const mkt of sportsbooksToProcess) {
				pushSportsbooksToQueryField(queryRow, 'book', mkt);
			}
			// Fall back to WagerWiz defaults when sharpBooks is empty — without a sharp
			// reference the backend returns no results.
			const sharpBooksToProcess = row.sharpBooks && row.sharpBooks.length > 0 ? row.sharpBooks : getWagerWizDisplayOptions();
			for (const mkt of sharpBooksToProcess) {
				pushSportsbooksToQueryField(queryRow, 'sharp', mkt);
			}

			queryRows.push(queryRow);
		}

		// Determine which endpoints to call based on markets
		const endpoints = determineEvEndpoints(queryRows);
		
		// Make parallel requests to all determined endpoints
		const requests = endpoints.map(endpoint =>
			axios.post(
				`${process.env.REACT_APP_BACKEND_URL}${endpoint}?email=${email}&is_live=${is_live}&page=${page}&limit=${limit}`,
				{ queryRows: queryRows },
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			).catch(error => {
				// Handle errors gracefully - return empty result for failed endpoint
				console.error(`Error calling ${endpoint}:`, error);
				// Check if it's a daily scan limit error
				if (error.response && typeof error.response.data === 'string' && error.response.data === 'exceeded daily scans limit') {
					return { data: 'exceeded daily scans limit' };
				}
				return { data: { data: [], pagination: { page: 1, limit: 0, total: 0, totalPages: 0, hasNext: false, hasPrev: false } } };
			})
		);

		const responses = await Promise.all(requests);
		
		// Check if any response indicates daily scan limit exceeded
		const scanLimitExceeded = responses.some(res => 
			res.data === 'exceeded daily scans limit' || 
			(res.data && typeof res.data === 'string' && res.data === 'exceeded daily scans limit')
		);
		
		if (scanLimitExceeded) {
			return 'exceeded daily scans limit';
		}

		// Combine all results
		let combinedData = [];
		let combinedPagination = {
			page: page,
			limit: limit,
			total: 0,
			totalPages: 0,
			hasNext: false,
			hasPrev: false
		};

		for (const response of responses) {
			if (response.data && typeof response.data === 'object') {
				// Handle paginated response format
				if (response.data.data && Array.isArray(response.data.data)) {
					combinedData = combinedData.concat(response.data.data);
					// Aggregate pagination info
					if (response.data.pagination) {
						combinedPagination.total += response.data.pagination.total || 0;
						// hasNext is true if any endpoint has more pages
						if (response.data.pagination.hasNext) {
							combinedPagination.hasNext = true;
						}
						// hasPrev is true if any endpoint has previous pages
						if (response.data.pagination.hasPrev) {
							combinedPagination.hasPrev = true;
						}
					}
				} else if (Array.isArray(response.data)) {
					// Legacy format (direct array)
					combinedData = combinedData.concat(response.data);
				}
			}
		}

		// Sort combined results by EV (descending) if available
		combinedData.sort((a, b) => {
			const evA = a.ev || a.ev_over || 0;
			const evB = b.ev || b.ev_over || 0;
			return evB - evA;
		});

		// Calculate combined pagination
		combinedPagination.total = combinedData.length;
		combinedPagination.totalPages = Math.ceil(combinedPagination.total / limit);

		// Return combined results in paginated format
		return {
			data: combinedData,
			pagination: combinedPagination
		};
	} catch (e) {
		console.error('ev screen paginated e: ', e);
		return false;
	}
};

export const getEvResults = async (email, sports) => {
	try {
		// const accessToken = JSON.parse(window.localStorage.getItem('user')).access_token;
		const token = window.localStorage.getItem('access_token');
		if (!token) {
			window.location.reload();
		}

		const userEvResults = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/ev_screen_results?email=${email}`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		});
		// parse results into an array
		// get the related links and games data and join it
		const parsedEvData = [];

		if (sports.includes('basketball') || sports.length === 0) {
			if (!userEvResults.data.user.response_loading_basketball) {
				const evdata = userEvResults.data.user.response_basketball;
				for (const evrow in evdata) {
					if (evdata[evrow].market === 'Point Spread' || evdata[evrow].market === 'Asian Handicap') {
						// console.log('evdata[evrow]: ', evdata[evrow]);
						try {
							evdata[evrow].id = evdata[evrow].outcome_id_normalized;
							evdata[evrow].bet_type = evdata[evrow].outcome_id_normalized.split(':')[1];
							evdata[evrow].bet_name = `${evdata[evrow].id.split(':')[2]}`;

							if (
								evdata[evrow].bet_price_over_negative_sharp &&
								evdata[evrow].ev_over_negative > evdata[evrow].ev_over_positive &&
								evdata[evrow].ev_over_negative > evdata[evrow].ev_under_negative &&
								evdata[evrow].ev_over_negative > evdata[evrow].ev_under_positive
							) {
								evdata[evrow].ev = evdata[evrow].ev_over_negative;
								evdata[evrow].quarter_kelly = evdata[evrow].quarter_kelly_over_negative;
								evdata[evrow].selected_sharp_price = evdata[evrow].bet_price_over_negative_sharp;
								evdata[evrow].sharp_prices = `${evdata[evrow].bet_price_over_negative_sharp?.toFixed(0)} / ${evdata[evrow].bet_price_under_positive_sharp?.toFixed(0) || ''}`;
								evdata[evrow].bet_price = evdata[evrow].bet_price_over_negative_dumb?.toFixed(0);
								evdata[evrow].type = 'bet_price_over_negative_sharp';

								parsedEvData.push(evdata[evrow]);
							} else if (
								evdata[evrow].bet_price_over_positive_sharp &&
								evdata[evrow].ev_over_positive > evdata[evrow].ev_over_negative &&
								evdata[evrow].ev_over_positive > evdata[evrow].ev_under_negative &&
								evdata[evrow].ev_over_positive > evdata[evrow].ev_under_positive
							) {
								evdata[evrow].ev = evdata[evrow].ev_over_negative;
								evdata[evrow].quarter_kelly = evdata[evrow].quarter_kelly_over_negative;
								evdata[evrow].selected_sharp_price = evdata[evrow].bet_price_over_positive_sharp;
								evdata[evrow].sharp_prices = `${evdata[evrow].bet_price_over_positive_sharp?.toFixed(0)} / ${evdata[evrow].bet_price_under_negative_sharp?.toFixed(0) || ''}`;
								evdata[evrow].bet_price = evdata[evrow].bet_price_over_positive_dumb?.toFixed(0);
								evdata[evrow].type = 'bet_price_over_positive_sharp';

								parsedEvData.push(evdata[evrow]);
							} else if (
								evdata[evrow].bet_price_under_negative_sharp &&
								evdata[evrow].ev_under_negative > evdata[evrow].ev_over_positive &&
								evdata[evrow].ev_under_negative > evdata[evrow].ev_over_negative &&
								evdata[evrow].ev_under_negative > evdata[evrow].ev_under_positive
							) {
								evdata[evrow].ev = evdata[evrow].ev_under_negative;
								evdata[evrow].quarter_kelly = evdata[evrow].quarter_kelly_under_negative;
								evdata[evrow].selected_sharp_price = evdata[evrow].bet_price_under_negative_sharp;
								evdata[evrow].sharp_prices = `${evdata[evrow].bet_price_under_negative_sharp?.toFixed(0)} / ${evdata[evrow].bet_price_over_positive_sharp?.toFixed(0) || ''}`;
								evdata[evrow].bet_price = evdata[evrow].bet_price_under_negative_dumb?.toFixed(0);
								evdata[evrow].type = 'bet_price_under_negative_sharp';

								parsedEvData.push(evdata[evrow]);
							} else if (
								evdata[evrow].bet_price_under_positive_sharp &&
								evdata[evrow].ev_under_positive > evdata[evrow].ev_over_negative &&
								evdata[evrow].ev_under_positive > evdata[evrow].ev_under_negative &&
								evdata[evrow].ev_under_positive > evdata[evrow].ev_over_positive
							) {
								evdata[evrow].ev = evdata[evrow].ev_under_positive;
								evdata[evrow].quarter_kelly = evdata[evrow].quarter_kelly_under_positive;
								evdata[evrow].selected_sharp_price = evdata[evrow].bet_price_under_positive_sharp;
								evdata[evrow].sharp_prices = `${evdata[evrow].bet_price_under_positive_sharp?.toFixed(0)} / ${evdata[evrow].bet_price_over_negative_sharp?.toFixed(0) || ''}`;
								evdata[evrow].bet_price = evdata[evrow].bet_price_under_positive_dumb?.toFixed(0);
								evdata[evrow].type = 'bet_price_under_positive_sharp';

								parsedEvData.push(evdata[evrow]);
							}
						} catch (e) {
							console.error(e);
						}
					} else {
						const formattedPriceOverSharp =
							evdata[evrow].bet_price_over_sharp > 0 ? `+${evdata[evrow].bet_price_over_sharp?.toFixed(0)}` : evdata[evrow].bet_price_over_sharp?.toFixed(0);
						const formattedPriceUnderSharp =
							evdata[evrow].bet_price_under_sharp > 0 ? `+${evdata[evrow].bet_price_under_sharp.toFixed(0)}` : evdata[evrow].bet_price_under_sharp?.toFixed(0);

						if (evdata[evrow].ev_over > evdata[evrow].ev_under) {
							evdata[evrow].ev = evdata[evrow].ev_over;
							evdata[evrow].quarter_kelly = evdata[evrow].quarter_kelly_over;
							evdata[evrow].bet_price =
								evdata[evrow].bet_price_over_dumb?.toFixed(0) > 0 ? `+${evdata[evrow].bet_price_over_dumb?.toFixed(0)}` : evdata[evrow].bet_price_over_dumb?.toFixed(0);
							evdata[evrow].selected_sharp_price = evdata[evrow].bet_price_over_sharp;
							const splitId = evdata[evrow].outcome_id_normalized.split(':');
							evdata[evrow].bet_name = `${splitId[2]} over ${splitId[3]}`;
							evdata[evrow].sharp_prices = `${formattedPriceOverSharp} / ${formattedPriceUnderSharp}`;
						} else {
							evdata[evrow].ev = evdata[evrow].ev_under;
							evdata[evrow].quarter_kelly = evdata[evrow].quarter_kelly_under;
							evdata[evrow].bet_price =
								evdata[evrow].bet_price_under_dumb?.toFixed(0) > 0 ? `+${evdata[evrow].bet_price_under_dumb?.toFixed(0)}` : evdata[evrow].bet_price_under_dumb?.toFixed(0);
							evdata[evrow].selected_sharp_price = evdata[evrow].bet_price_under_sharp;
							const splitId = evdata[evrow].outcome_id_normalized.split(':');
							evdata[evrow].bet_name = `${splitId[2]} under ${splitId[3]}`;
							evdata[evrow].sharp_prices = `${formattedPriceUnderSharp} / ${formattedPriceOverSharp}`;
						}

						parsedEvData.push(evdata[evrow]);
					}
				}
			}
		}

		if (sports.some((sport) => sport !== 'soccer' && sport !== 'basketball') || sports.length === 0) {
			if (!userEvResults.data.user.response_loading_other) {
				const evdata = userEvResults.data.user.response_other;
				for (const evrow in evdata) {
					const formattedPriceOverSharp =
						evdata[evrow].bet_price_over_sharp > 0 ? `+${evdata[evrow].bet_price_over_sharp?.toFixed(0)}` : evdata[evrow].bet_price_over_sharp?.toFixed(0);
					const formattedPriceUnderSharp =
						evdata[evrow].bet_price_under_sharp > 0 ? `+${evdata[evrow].bet_price_under_sharp.toFixed(0)}` : evdata[evrow].bet_price_under_sharp?.toFixed(0);

					if (evdata[evrow].ev_over > evdata[evrow].ev_under) {
						evdata[evrow].ev = evdata[evrow].ev_over;
						evdata[evrow].quarter_kelly = evdata[evrow].quarter_kelly_over;
						evdata[evrow].bet_price =
							evdata[evrow].bet_price_over_dumb?.toFixed(0) > 0 ? `+${evdata[evrow].bet_price_over_dumb?.toFixed(0)}` : evdata[evrow].bet_price_over_dumb?.toFixed(0);
						evdata[evrow].selected_sharp_price = evdata[evrow].bet_price_over_sharp;
						const splitId = evdata[evrow].outcome_id_normalized.split(':');
						evdata[evrow].bet_name = `${splitId[2]} over ${splitId[3]}`;
						evdata[evrow].sharp_prices = `${formattedPriceOverSharp} / ${formattedPriceUnderSharp}`;
					} else {
						evdata[evrow].ev = evdata[evrow].ev_under;
						evdata[evrow].quarter_kelly = evdata[evrow].quarter_kelly_under;
						evdata[evrow].bet_price =
							evdata[evrow].bet_price_under_dumb?.toFixed(0) > 0 ? `+${evdata[evrow].bet_price_under_dumb?.toFixed(0)}` : evdata[evrow].bet_price_under_dumb?.toFixed(0);
						evdata[evrow].selected_sharp_price = evdata[evrow].bet_price_under_sharp;
						const splitId = evdata[evrow].outcome_id_normalized.split(':');
						evdata[evrow].bet_name = `${splitId[2]} under ${splitId[3]}`;
						evdata[evrow].sharp_prices = `${formattedPriceUnderSharp} / ${formattedPriceOverSharp}`;
					}

					parsedEvData.push(evdata[evrow]);
				}
			}
		}

		// iterate through all results and get the game and link data and attach to correct row

		const gameIds = [];
		const evDataIds = [];
		const evDataIdsSharp = [];

		for (const evdataRow of parsedEvData) {
			// Determine which side (over/under) has better EV and construct outcome ID accordingly
			// Skip for Point Spread and Asian Handicap as they have different logic
			if (evdataRow.outcome_id_normalized && evdataRow.market !== 'Point Spread' && evdataRow.market !== 'Asian Handicap') {
				const splitoutcomeid = evdataRow.outcome_id_normalized.split(':');
				if (splitoutcomeid.length >= 5) {
					// Determine which side to use based on EV
					const side = (evdataRow.ev_over > evdataRow.ev_under) ? 'over' : 'under';
					const sportsbook = splitoutcomeid[splitoutcomeid.length - 1];
					// Reconstruct outcome ID with the correct side
					const outcomeId = `${splitoutcomeid[0]}:${splitoutcomeid[1]}:${splitoutcomeid[2]}:${splitoutcomeid[3]}:${side}:${sportsbook}`;
					evDataIds.push(outcomeId);
				} else {
					// Fallback to original if format is unexpected
					evDataIds.push(evdataRow.outcome_id_normalized);
				}
			} else if (evdataRow.outcome_id_normalized) {
				// For Point Spread/Asian Handicap, use the outcome_id_normalized as-is
				evDataIds.push(evdataRow.outcome_id_normalized);
			}

			if (!gameIds.some((gameId) => gameId === evdataRow.game_id)) {
				gameIds.push(evdataRow.game_id);
			}

			// Handle cases where sharp_book might be undefined or null
			if (evdataRow.sharp_book && evdataRow.outcome_id_normalized) {
				const sharpBook = evdataRow.sharp_book.split(',');
				const splitoutcomeid = evdataRow.outcome_id_normalized.split(':');
				if (splitoutcomeid.length >= 4) {
					// For sharp links, also use the correct side based on EV
					const side = (evdataRow.market !== 'Point Spread' && evdataRow.market !== 'Asian Handicap' && evdataRow.ev_over > evdataRow.ev_under) ? 'over' : 
					             (evdataRow.market !== 'Point Spread' && evdataRow.market !== 'Asian Handicap' && evdataRow.ev_over <= evdataRow.ev_under) ? 'under' : 
					             splitoutcomeid[4] || 'over';
					const sharpOutcomeId = `${splitoutcomeid[0]}:${splitoutcomeid[1]}:${splitoutcomeid[2]}:${splitoutcomeid[3]}:${side}:${sharpBook[sharpBook.length - 1]}`;
					evDataIdsSharp.push(sharpOutcomeId);
				}
			}
		}
		// console.log('gameIds: ', gameIds);
		// console.log('evDataIds: ', evDataIds);

		if (evDataIds.length) {
			const gamesData = await getGamesData(gameIds, token);
			// console.log('gamesData: ', gamesData);
			const linksData = await getLinksData(evDataIds, sports);
			const sharpLinksData = await getLinksData(evDataIdsSharp, sports);

			// iterate through each evrow and match the data

			for (const row of parsedEvData) {
				// Construct the outcome ID with the correct side (over/under) based on EV
				let linkOutcomeId = row.outcome_id_normalized;
				if (row.outcome_id_normalized && row.market !== 'Point Spread' && row.market !== 'Asian Handicap') {
					const splitoutcomeid = row.outcome_id_normalized.split(':');
					if (splitoutcomeid.length >= 5) {
						const side = (row.ev_over > row.ev_under) ? 'over' : 'under';
						const sportsbook = splitoutcomeid[splitoutcomeid.length - 1];
						linkOutcomeId = `${splitoutcomeid[0]}:${splitoutcomeid[1]}:${splitoutcomeid[2]}:${splitoutcomeid[3]}:${side}:${sportsbook}`;
					}
				}
				const link = linksData[linkOutcomeId] || null;

				// Handle cases where sharp_book might be undefined or null
				let sharpLink = null;
				if (row.sharp_book && row.outcome_id_normalized) {
					const sharpBook = row.sharp_book.split(',');
					const splitoutcomeid = row.outcome_id_normalized.split(':');
					if (splitoutcomeid.length >= 4) {
						// Use the correct side based on EV
						const side = (row.market !== 'Point Spread' && row.market !== 'Asian Handicap' && row.ev_over > row.ev_under) ? 'over' : 
						             (row.market !== 'Point Spread' && row.market !== 'Asian Handicap' && row.ev_over <= row.ev_under) ? 'under' : 
						             splitoutcomeid[4] || 'over';
						const sharpOutcomeId = `${splitoutcomeid[0]}:${splitoutcomeid[1]}:${splitoutcomeid[2]}:${splitoutcomeid[3]}:${side}:${sharpBook[sharpBook.length - 1]}`;
						sharpLink = sharpLinksData[sharpOutcomeId] || null;
					}
				}
				const game = gamesData.games.find((g) => g.game_id === row.game_id);

				row.link = link;
				row.sharpLink = sharpLink;
				// console.log('sharpLink: ', sharpLink);
				row.game = game;
			}
		}
		// console.log('parsedEvData: ', parsedEvData);

		return { user: userEvResults.data, data: parsedEvData };
	} catch (e) {
		console.error(e);
		return false;
	}
};

export const getLinksData = async (outcome_ids, sports, include_history = false) => {
	try {
		const accessToken = window.localStorage.getItem('access_token');
		if (!accessToken) {
			window.location.reload();
		}

		const linksData = (
			await axios.post(
				`${process.env.REACT_APP_BACKEND_URL}/links?sports=${sports?.join(',')}&include_history=${include_history}`,
				{
					outcome_id_normalized: outcome_ids,
				},
				{
					headers: {
						Authorization: `Bearer ${accessToken}`,
					},
				}
			)
		).data;

		return linksData;
	} catch (e) {
		console.log('e: ', e);
		return {};
	}
};

export const getGamesData = async (game_ids, token) => {
	// const accessToken = window.localStorage.getItem('access_token');

	const relatedOdds = (
		await axios.post(`${process.env.REACT_APP_BACKEND_URL}/games`, game_ids, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
	).data;

	return relatedOdds;
};

export const getGamesDataFree = async (game_ids) => {
	try {
		const result = (
			await axios.post(`${process.env.REACT_APP_BACKEND_URL}/games`, game_ids)
		).data;
		return result;
	} catch (e) {
		console.error('getGamesDataFree error:', e);
		return { games: [] };
	}
};
